import React from "react";
import { cn } from "@/lib/utils";
import { hm, initials, QueueEntryApi } from "../../services/queue/types";
import { waitLevel, WaitLevel } from "../../services/queue/stats";

/** Petits éléments d'affichage partagés par les vues médecin et secrétaire. */

const WAIT_LEVEL_CLASSES: Record<WaitLevel, string> = {
  normal: "bg-slate-100 text-slate-600",
  warning: "bg-amber-50 text-amber-800",
  alert: "bg-rose-50 text-rose-700",
};

/** Temps d'attente coloré selon les seuils de `services/queue/stats`. */
export const WaitTimeBadge: React.FC<{ minutes: number | null; suffix?: string }> = ({ minutes, suffix = "" }) => {
  if (minutes == null) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums",
        WAIT_LEVEL_CLASSES[waitLevel(minutes)]
      )}
    >
      {minutes} min{suffix}
    </span>
  );
};

type PatientAvatarProps = {
  entry: Pick<QueueEntryApi, "patientFirstName" | "patientLastName">;
  size?: "md" | "lg";
  /** `muted` : avatar neutre pour les patients qui ne sont pas « suivant ». */
  muted?: boolean;
};

/** Avatar à initiales du design system (dégradé emerald). */
export const PatientAvatar: React.FC<PatientAvatarProps> = ({ entry, size = "md", muted = false }) => (
  <span
    className={cn(
      "flex shrink-0 items-center justify-center rounded-full font-semibold",
      size === "lg" ? "h-16 w-16 text-xl ring-4 ring-emerald-50" : "h-10 w-10 text-sm ring-2 ring-white shadow-sm",
      muted ? "bg-slate-100 text-slate-700" : "bg-gradient-to-br from-emerald-500 to-green-600 text-white"
    )}
    aria-hidden
  >
    {initials(entry.patientFirstName, entry.patientLastName)}
  </span>
);

/** « RDV HH:MM » ou « Sans RDV ». */
export const AppointmentPill: React.FC<{ entry: Pick<QueueEntryApi, "walkIn" | "appointmentTime"> }> = ({ entry }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600">
    <i className={cn("bx text-sm", entry.walkIn ? "bx-walk" : "bx-calendar-check")} aria-hidden />
    {entry.walkIn ? "Sans RDV" : `RDV ${hm(entry.appointmentTime)}`}
  </span>
);

type StatTileProps = {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: "neutral" | "warning" | "alert";
};

const STAT_TONE_CLASSES = {
  neutral: { card: "border-slate-200 bg-white shadow-sm", label: "text-slate-500", value: "text-slate-900", hint: "text-slate-500" },
  warning: { card: "border-amber-200 bg-amber-50", label: "text-amber-800", value: "text-amber-900", hint: "text-amber-800" },
  alert: { card: "border-red-200 bg-red-50", label: "text-red-700", value: "text-red-700", hint: "text-red-700" },
};

/** Carte d'indicateur (KPI). */
export const StatTile: React.FC<StatTileProps> = ({ label, value, hint, tone = "neutral" }) => {
  const toneClasses = STAT_TONE_CLASSES[tone];
  return (
    <div className={cn("rounded-2xl border px-5 py-4", toneClasses.card)}>
      <p className={cn("text-[11px] uppercase tracking-wider font-semibold", toneClasses.label)}>{label}</p>
      <p className={cn("mt-2 text-3xl font-bold tabular-nums", toneClasses.value)}>{value}</p>
      {hint && <p className={cn("mt-0.5 text-xs truncate", toneClasses.hint)}>{hint}</p>}
    </div>
  );
};

/** État vide commun. */
export const QueueEmptyState: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-200 bg-white py-20 text-center">
    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
      <i className="bx bx-coffee text-3xl" aria-hidden />
    </div>
    <p className="text-lg font-semibold text-slate-900">{title}</p>
    <p className="mt-1 max-w-xs text-sm text-slate-500">{description}</p>
  </div>
);
